/* Gunner collaboration PDP: behaviour for the gunner-pdp-* sections.
   Loaded by every section (snippet gunner-pdp-assets); runs once. */
(() => {
  if (window.gunnerPdpLoaded) return;
  window.gunnerPdpLoaded = true;

  const desktop = window.matchMedia('(min-width: 1000px)');  // stats are an accordion below this

  /* ---------- Modals: YouTube video and image popups ---------- */

  function openModal(inner) {
    const dialog = document.createElement('dialog');
    dialog.className = 'gk-modal';
    dialog.innerHTML = `<button type="button" class="gk-unbutton gk-modal__close" aria-label="Close">&times;</button>${inner}`;
    const close = () => dialog.close();
    dialog.addEventListener('close', () => dialog.remove());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('.gk-modal__close')) close();
    });
    document.body.append(dialog);
    dialog.showModal();
  }

  document.addEventListener('click', (event) => {
    const videoTrigger = event.target.closest('[data-gk-youtube]');
    if (videoTrigger) {
      event.preventDefault();
      const id = encodeURIComponent(videoTrigger.dataset.gkYoutube);
      const title = videoTrigger.dataset.gkTitle || 'Video';
      openModal(`<div class="gk-modal__frame"><iframe src="https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1&modestbranding=1" title="${title.replace(/"/g, '&quot;')}" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe></div>`);
      return;
    }

    const imageTrigger = event.target.closest('[data-gk-image]');
    if (imageTrigger) {
      event.preventDefault();
      const img = document.createElement('img');
      img.className = 'gk-modal__image';
      img.src = imageTrigger.dataset.gkImage;
      img.alt = imageTrigger.dataset.gkAlt || '';
      openModal(img.outerHTML);
    }
  });

  /* ---------- Kennel Walkthrough button inside the native gallery ---------- */

  function placeWalkthrough() {
    const source = document.querySelector('.gk-walkthrough-source .gk-walkthrough');
    const gallery = document.querySelector('.shopify-section--main-product .product-gallery');
    if (!gallery) return;
    gallery.querySelectorAll(':scope > .gk-walkthrough').forEach((button) => {
      if (!source || button.dataset.gkYoutube !== source.dataset.gkYoutube || button.textContent !== source.textContent) button.remove();
    });
    if (source && !gallery.querySelector(':scope > .gk-walkthrough')) {
      gallery.append(source.cloneNode(true));
    }
  }

  /* ---------- Sliders ---------- */

  function initSlider(root) {
    const track = root.querySelector('.gk-slider__track');
    const prev = root.querySelector('[data-gk-prev]');
    const next = root.querySelector('[data-gk-next]');
    if (!track || track.dataset.gkReady) return;
    track.dataset.gkReady = 'true';

    const step = () => {
      const item = track.firstElementChild;
      if (!item) return track.clientWidth;
      return item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- Tech details: size tabs + hotspots ---------- */

  function initTech(root) {
    if (root.dataset.gkReady) return;
    root.dataset.gkReady = 'true';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    const tabList = root.querySelector('.gk-tech__tabs');

    const closeHotspots = (except) => {
      root.querySelectorAll('.gk-hotspot.is-open').forEach((spot) => {
        if (spot === except) return;
        spot.classList.remove('is-open');
        spot.querySelector('.gk-hotspot__dot')?.setAttribute('aria-expanded', 'false');
      });
      if (!except) root.querySelectorAll('.gk-tech__mobile-card').forEach((card) => { card.hidden = true; });
    };

    const select = (tab, focus) => {
      tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
      closeHotspots();
      if (focus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        const move = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!move) return;
        event.preventDefault();
        select(tabs[(index + move + tabs.length) % tabs.length], true);
      });
    });

    root.querySelector('[data-gk-prev]')?.addEventListener('click', () => tabList.scrollBy({ left: -280, behavior: 'smooth' }));
    root.querySelector('[data-gk-next]')?.addEventListener('click', () => tabList.scrollBy({ left: 280, behavior: 'smooth' }));

    root.addEventListener('click', (event) => {
      const dot = event.target.closest('.gk-hotspot__dot');
      if (!dot) {
        if (!event.target.closest('.gk-hotspot__card')) closeHotspots();
        return;
      }
      const spot = dot.closest('.gk-hotspot');
      const opening = !spot.classList.contains('is-open');
      closeHotspots(spot);
      spot.classList.toggle('is-open', opening);
      dot.setAttribute('aria-expanded', String(opening));

      const mobileCard = spot.closest('[role="tabpanel"]').querySelector('.gk-tech__mobile-card');
      if (mobileCard) {
        mobileCard.hidden = !opening;
        if (opening) {
          mobileCard.querySelector('h4').textContent = spot.querySelector('.gk-hotspot__title').textContent;
          mobileCard.querySelector('div').innerHTML = spot.querySelector('.gk-hotspot__text').innerHTML;
        }
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeHotspots();
    });
  }

  /* ---------- Stats (accordion on mobile) ---------- */

  function initStats(root) {
    if (root.dataset.gkReady) return;
    root.dataset.gkReady = 'true';
    root.querySelectorAll('.gk-stats__head').forEach((head) => {
      head.addEventListener('click', () => {
        if (desktop.matches) return;
        const item = head.closest('.gk-stats__item');
        const opening = !item.classList.contains('is-open');
        root.querySelectorAll('.gk-stats__item').forEach((other) => {
          other.classList.remove('is-open');
          other.querySelector('.gk-stats__head').setAttribute('aria-expanded', 'false');
        });
        item.classList.toggle('is-open', opening);
        head.setAttribute('aria-expanded', String(opening));
      });
    });
  }

  /* ---------- Comparison tooltips ---------- */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.gk-compare__tip-toggle');
    document.querySelectorAll('.gk-compare__tip-panel:not([hidden])').forEach((panel) => {
      if (toggle && panel.id === toggle.getAttribute('aria-controls')) return;
      panel.hidden = true;
      document.querySelector(`[aria-controls="${panel.id}"]`)?.setAttribute('aria-expanded', 'false');
    });
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    panel.hidden = !panel.hidden;
    toggle.setAttribute('aria-expanded', String(!panel.hidden));
  });

  /* ---------- Smooth scroll for in-page links (e.g. Kennel Dimensions) ---------- */

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.gk-section a[href^="#"]');
    if (!link || link.getAttribute('href').length < 2) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ---------- Init + theme editor support ---------- */

  function init() {
    document.querySelectorAll('.gk-slider').forEach(initSlider);
    document.querySelectorAll('.gk-tech').forEach(initTech);
    document.querySelectorAll('.gk-stats').forEach(initStats);
    placeWalkthrough();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => setTimeout(init, 0));

  // The product section re-renders its gallery when the variant changes; put the button back.
  const productSection = document.querySelector('.shopify-section--main-product');
  if (productSection) {
    new MutationObserver(placeWalkthrough).observe(productSection, { childList: true, subtree: true });
  }
})();
